import { TableTextStyles, tableTextStyle, tableTextColor } from './table-text-styles';
import { TableStylePreset, DEFAULT_TABLE_STYLES } from '../models/template-builder/table-styles.model';
import { renderListEmailWidget } from './list-email-widget-renderer';
import { renderSocialMediaWidget } from './social-media-email-widget-renderer';
import { escapeHtml, mapHorizontalAlignment, mapVerticalAlignment, styleString } from './email-template-builder.utils';
import {
  applyPerLinkEmailStyles,
  createLinkHoverStyleCollector,
  LinkHoverStyleCollector,
} from './email-template-link-styles.utils';

export type EmailTemplateExportOptions = {
  includeMobileStyles?: boolean;
  listIconPaths?: Record<string, string>;
  contentMaxWidth?: number;
  cssVariables?: Record<string, string | number>;
};

export type EmailTemplateExportResult = {
  html: string;
  helpers: string[];
  partials: Record<string, string>;
  warnings: string[];
};

interface ResolveTableOptions {
  textStyles?: TableTextStyles;
  title: string;
  colorScheme: { header: string; color1: string; color2: string };
  includePreferences?: boolean;
}

const WIDGET_TEXT = 'text';
const WIDGET_HEADING = 'heading';
const WIDGET_BUTTON = 'button';
const WIDGET_IMAGE = 'image';
const WIDGET_SEPARATOR = 'separator';
const WIDGET_SPACER = 'spacer';
const WIDGET_EMAIL_REGISTRATION_DETAILS_TABLE = 'emailRegistrationDetailsTable';
const WIDGET_EMAIL_EXCURSIONS_TABLE = 'emailExcursionsTable';
const WIDGET_EMAIL_TRANSACTIONS_TABLE = 'emailTransactionsTable';
const WIDGET_EMAIL_ACTIVITIES_FOR_DAY = 'emailActivitiesForDay';
const WIDGET_EMAIL_FORM_RESULTS_TABLE = 'emailFormResultsTable';
const WIDGET_EMAIL_CONFERENCE_IMAGE = 'emailConferenceImage';
const WIDGET_SOCIAL_MEDIA = 'socialMedia';

const ROW_BLOCK = 'block';
const ROW_MODULE = 'module';

const DEFAULT_CONTENT_MAX_WIDTH = 760;
const DEFAULT_HEADING_MARGIN = '0';
const DEFAULT_FONT_FAMILY = "'Helvetica Neue'";
const DEFAULT_TABLE_TEXT_COLOR = '#111827';
const DEFAULT_TABLE_BORDER_COLOR = '#e5e7eb';
const DEFAULT_TABLE_HEADER_BG = '#f3f4f6';
const DEFAULT_TABLE_ZEBRA_BG = '#f9fafb';
const DEFAULT_TABLE_GROUP_BG = '#f8fafc';
const DEFAULT_EMAIL_TABLE_STYLE = TableStylePreset.Gray;
const DEFAULT_EMAIL_TABLE_STYLES = DEFAULT_TABLE_STYLES;

export function buildEmailHandlebarsTemplate(
  template: any,
  options?: EmailTemplateExportOptions,
): EmailTemplateExportResult {
  const warnings: string[] = [];
  const helpers = new Set<string>();
  const partials: Record<string, string> = {};

  const commonSettings = template?.commonSettings ?? {};
  const contentMaxWidth = options?.contentMaxWidth ?? commonSettings?.contentMaxWidth ?? DEFAULT_CONTENT_MAX_WIDTH;
  const backgroundColor = commonSettings?.backgroundColor ?? '#ffffff';
  const textColor = commonSettings?.textColor ?? '#333';
  const fontFamily = commonSettings?.fontFamily ?? 'Helvetica, Arial, sans-serif';

  const cssVariables = buildCssVariablesMap(commonSettings, options?.cssVariables);
  const linkHoverCollector = createLinkHoverStyleCollector();

  const rowsHtml = (template?.rows ?? [])
    .filter(Boolean)
    .map(row =>
      renderRow(
        row,
        {
          contentMaxWidth,
          backgroundColor,
          textColor,
          fontFamily,
          socialMediaSettings: template?.socialMediaSettings,
          linkHoverCollector,
          listIconPaths: options?.listIconPaths,
        },
        cssVariables,
        helpers,
        warnings,
      ),
    )
    .join('');

  const styleRules: string[] = [];

  if (options?.includeMobileStyles !== false) {
    styleRules.push(
      '@media only screen and (max-width: 600px) {' +
        ' .tb-content { width: 100% !important; }' +
        ' .tb-col { display: block !important; width: 100% !important; max-width: 100% !important; }' +
        ' .tb-list-card { display:block !important; width:100% !important; box-sizing:border-box; padding-right:0 !important; }' +
        ' }',
    );
  }

  const linkPseudoStyles = buildLinkPseudoClassStyles(cssVariables);
  if (linkPseudoStyles) {
    styleRules.push(linkPseudoStyles);
  }

  if (linkHoverCollector.rules.length) {
    styleRules.push(linkHoverCollector.rules.join('\n  '));
  }

  const styleBlock = styleRules.length ? `\n<style type=\"text/css\">\n  ${styleRules.join('\n  ')}\n</style>\n` : '';

  const html = `<!doctype html>\n<html>\n<head>\n<meta charset=\"utf-8\">\n<meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">${styleBlock}</head>\n<body style=\"margin:0;padding:0;background-color:${backgroundColor};color:${textColor};font-family:${fontFamily};\">\n<table role=\"presentation\" width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"width:100%;background-color:${backgroundColor};\">\n  <tr>\n    <td align=\"center\" style=\"padding:0;margin:0;\">\n      ${rowsHtml}\n    </td>\n  </tr>\n</table>\n</body>\n</html>`;

  const htmlWithListStyles = applyEmailListStyles(html);
  const htmlWithResolvedCssVars = replaceCssVariables(htmlWithListStyles, cssVariables, {
    fallbackValue: commonSettings?.textColor ?? '#333',
  });

  return {
    html: htmlWithResolvedCssVars,
    helpers: Array.from(helpers),
    partials,
    warnings,
  };
}

function buildCssVariablesMap(
  commonSettings: any,
  cssVariables?: Record<string, string | number>,
): Record<string, string | number> {
  const map: Record<string, string | number> = {
    '--textColor': commonSettings?.textColor,
    '--mainColor': commonSettings?.textColor,
    '--backgroundColor': commonSettings?.backgroundColor,
    '--contentBackgroundColor': commonSettings?.contentBackgroundColor ?? '#fff',
    '--contentMaxWidth': commonSettings?.contentMaxWidth,
    '--global-font-family': commonSettings?.fontFamily,
    ...(cssVariables ?? {}),
  };

  Object.keys(map).forEach(key => {
    if (map[key] === undefined || map[key] === null) {
      delete map[key];
    }
  });

  const isVarValue = (value: unknown) => typeof value === 'string' && value.trim().toLowerCase().startsWith('var(');

  if (!map['--textColor'] || isVarValue(map['--textColor'])) {
    const fallback = commonSettings?.textColor;
    map['--textColor'] = !isVarValue(fallback) ? fallback : '#333';
  }

  if (!map['--mainColor'] || isVarValue(map['--mainColor'])) {
    const fallback = map['--textColor'] ?? commonSettings?.textColor;
    map['--mainColor'] = !isVarValue(fallback) ? fallback : '#333';
  }

  return map;
}

function resolveCssVarValue(value: string, cssVariables: Record<string, string | number>, depth = 0): string {
  if (depth > 5) {
    return value;
  }

  const match = value.match(/^var\(\s*--([A-Za-z0-9_-]+)\s*(?:,\s*([^)]+))?\)$/);
  if (!match) {
    return value;
  }

  const key = `--${match[1]}`;
  const fallback = match[2] ? match[2].trim() : '';
  const next = cssVariables[key];
  if (next === undefined || next === null || next === '') {
    return fallback || value;
  }

  const nextValue = String(next);
  if (nextValue.trim().toLowerCase().startsWith('var(')) {
    return resolveCssVarValue(nextValue, cssVariables, depth + 1);
  }

  return nextValue;
}

function replaceCssVariables(
  html: string,
  cssVariables: Record<string, string | number>,
  options?: { fallbackValue?: string },
): string {
  if (!html || !cssVariables || Object.keys(cssVariables).length === 0) {
    return html;
  }

  return html.replace(/var\(\s*--([A-Za-z0-9_-]+)\s*(?:,\s*([^)]+))?\)/g, (match, name, fallback) => {
    const key = `--${name}`;
    const value = cssVariables[key];
    if (value === undefined || value === null || value === '') {
      return fallback ? String(fallback).trim() : options?.fallbackValue ?? 'inherit';
    }
    const resolved = resolveCssVarValue(String(value), cssVariables);
    return resolved ?? String(value) ?? options?.fallbackValue ?? 'inherit';
  });
}

function mergeStyles(base: string, extra: string): string {
  if (!extra) {
    return base;
  }
  if (!base) {
    return extra;
  }
  return `${base.replace(/;+$/, '')};${extra}`;
}

function normalizeFontFamily(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.replace(/\"/g, "'");
}

function buildTypographyStyles(
  widgetType: string,
  cssVariables: Record<string, string | number>,
): Record<string, string> {
  const isHeading = widgetType === WIDGET_HEADING;
  const prefix = isHeading ? '--heading-1-' : '--paragraph-';

  const fontSize = resolveVar(cssVariables[`${prefix}font-size`], cssVariables);
  const lineHeight = resolveVar(cssVariables[`${prefix}line-height`], cssVariables);
  const fontFamily =
    normalizeFontFamily(resolveVar(cssVariables[`${prefix}font-family`], cssVariables)) ?? DEFAULT_FONT_FAMILY;
  const color = resolveVar(cssVariables[`${prefix}color`], cssVariables) ?? '#289fe7';
  const backgroundColor = resolveVar(cssVariables[`${prefix}background-color`], cssVariables);

  const styles: Record<string, string> = {
    'font-family': String(fontFamily),
    color: String(color),
  };

  if (fontSize) {
    styles['font-size'] = String(fontSize);
  }
  if (lineHeight) {
    styles['line-height'] = String(lineHeight);
  }
  if (backgroundColor) {
    styles['background-color'] = String(backgroundColor);
  }

  if (isHeading) {
    const fontWeight = resolveVar(cssVariables['--heading-1-font-weight'], cssVariables);
    const fontStyle = resolveVar(cssVariables['--heading-1-font-style'], cssVariables);
    const textDecoration = resolveVar(cssVariables['--heading-1-text-decoration'], cssVariables);

    if (fontWeight) {
      styles['font-weight'] = String(fontWeight);
    }
    if (fontStyle) {
      styles['font-style'] = String(fontStyle);
    }
    if (textDecoration) {
      styles['text-decoration'] = String(textDecoration);
    }
  }

  return styles;
}

function resolveVar(value: unknown, cssVariables: Record<string, string | number>): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const str = String(value);
  if (!str.trim().toLowerCase().startsWith('var(')) {
    return str;
  }
  return resolveCssVarValue(str, cssVariables);
}

// color, text-decoration and text-decoration-style are intentionally omitted here:
// they are inlined per-link (including "From Global" links) by applyPerLinkEmailStyles
// so the link receives matching inline style and #link-<n>:hover/:active rules.
function buildLinkTagStyles(
  cssVariables: Record<string, string | number>,
  fallback?: {
    fontSize?: string;
    fontFamily?: string;
  },
): string {
  const defaultLinkFontSize = '40px';
  const defaultLinkFontFamily = DEFAULT_FONT_FAMILY;

  return styleString({
    'font-size':
      resolveVar(cssVariables['--link-font-size'], cssVariables) ?? fallback?.fontSize ?? defaultLinkFontSize,
    'font-family':
      normalizeFontFamily(resolveVar(cssVariables['--link-font-family'], cssVariables)) ??
      fallback?.fontFamily ??
      defaultLinkFontFamily,
    'background-color': resolveVar(cssVariables['--link-background-color'], cssVariables) ?? 'transparent',
  });
}

function buildParagraphTagStyles(cssVariables: Record<string, string | number>): Record<string, string> {
  const defaultFontSize = '40px';
  const defaultLineHeight = '1.2';
  const defaultFontFamily = DEFAULT_FONT_FAMILY;
  const defaultColor = '#289fe7';

  const fontSize = resolveVar(cssVariables['--paragraph-font-size'], cssVariables) ?? defaultFontSize;
  const lineHeight = resolveVar(cssVariables['--paragraph-line-height'], cssVariables) ?? defaultLineHeight;
  const fontFamily =
    normalizeFontFamily(resolveVar(cssVariables['--paragraph-font-family'], cssVariables)) ?? defaultFontFamily;
  const color = resolveVar(cssVariables['--paragraph-color'], cssVariables) ?? defaultColor;
  const backgroundColor = resolveVar(cssVariables['--paragraph-background-color'], cssVariables) ?? 'transparent';
  const textAlign = resolveVar(cssVariables['--paragraph-text-align'], cssVariables);

  const paragraphStyles = styleString({
    'font-size': fontSize,
    'line-height': lineHeight,
    'font-family': fontFamily,
    color: color,
    'background-color': backgroundColor,
    'text-align': textAlign,
  });

  const linkStyles = buildLinkTagStyles(cssVariables, {
    fontSize,
    fontFamily,
  });

  return {
    p: paragraphStyles,
    ul: paragraphStyles,
    ol: paragraphStyles,
    li: paragraphStyles,
    a: linkStyles,
    'span[data-styles-container]': styleString({
      display: 'block',
      'background-color': backgroundColor,
    }),
  };
}

function buildLinkPseudoClassStyles(cssVariables: Record<string, string | number>): string {
  const defaultColor = '#289fe7';

  const color = resolveVar(cssVariables['--active-link-color'], cssVariables) ?? defaultColor;
  const backgroundColor = resolveVar(cssVariables['--active-link-background-color'], cssVariables) ?? 'transparent';
  const textDecoration = resolveVar(cssVariables['--active-link-text-decoration'], cssVariables);
  const textDecorationStyle = resolveVar(cssVariables['--active-link-text-decoration-style'], cssVariables);

  const style = styleString({
    color: color,
    'background-color': backgroundColor,
    'text-decoration': textDecoration,
    'text-decoration-style': textDecorationStyle,
  });

  if (!style) {
    return '';
  }

  return `a:hover, a:active { ${style} }`;
}

function buildHeadingTagStyles(cssVariables: Record<string, string | number>): Record<string, string> {
  const map: Record<string, string> = {};

  for (let level = 1; level <= 6; level += 1) {
    const prefix = `--heading-${level}-`;
    const defaultFontSize = `${46 - level * 2}px`;
    const defaultLineHeight = '1.2';
    const defaultFontFamily = DEFAULT_FONT_FAMILY;
    const defaultColor = '#289fe7';
    const fontSize = resolveVar(cssVariables[`${prefix}font-size`], cssVariables) ?? defaultFontSize;
    const lineHeight = resolveVar(cssVariables[`${prefix}line-height`], cssVariables) ?? defaultLineHeight;
    const fontFamily =
      normalizeFontFamily(resolveVar(cssVariables[`${prefix}font-family`], cssVariables)) ?? defaultFontFamily;
    const color = resolveVar(cssVariables[`${prefix}color`], cssVariables) ?? defaultColor;
    const backgroundColor = resolveVar(cssVariables[`${prefix}background-color`], cssVariables) ?? 'transparent';
    const fontWeight = resolveVar(cssVariables[`${prefix}font-weight`], cssVariables);
    const fontStyle = resolveVar(cssVariables[`${prefix}font-style`], cssVariables);
    const textDecoration = resolveVar(cssVariables[`${prefix}text-decoration`], cssVariables);
    const textAlign = resolveVar(cssVariables[`${prefix}text-align`], cssVariables);

    map[`h${level}`] = styleString({
      'font-size': fontSize,
      'line-height': lineHeight,
      'font-family': fontFamily,
      color: color,
      'background-color': backgroundColor,
      'font-weight': fontWeight,
      'font-style': fontStyle,
      'text-decoration': textDecoration,
      'text-align': textAlign,
    });
  }

  map['span[data-styles-container]'] = styleString({
    display: 'block',
  });

  const headingFontSize = resolveVar(cssVariables['--heading-1-font-size'], cssVariables) ?? '44px';
  const headingFontFamily =
    normalizeFontFamily(resolveVar(cssVariables['--heading-1-font-family'], cssVariables)) ?? DEFAULT_FONT_FAMILY;

  map['a'] = buildLinkTagStyles(cssVariables, {
    fontSize: headingFontSize,
    fontFamily: headingFontFamily,
  });

  return map;
}

function applyInlineStylesToHtml(html: string, tagStyles: Record<string, string>): string {
  if (!html || !tagStyles) {
    return html;
  }

  const result = Object.entries(tagStyles).reduce((acc, [tag, style]) => {
    if (!style) {
      return acc;
    }

    if (tag.includes('[')) {
      return applyAttributeSelectorStyles(acc, tag, style);
    }

    const regex = new RegExp(`<${tag}(\\s[^>]*?)?>`, 'gi');
    return acc.replace(regex, match => {
      if (/style\s*=/.test(match)) {
        return match.replace(/style\s*=\s*([\"'])(.*?)\1/i, (_m, quote, existing) => {
          const combined = tag === 'a' ? mergeStyles(style, existing) : mergeStyles(existing, style);
          return `style=${quote}${combined}${quote}`;
        });
      }

      const hasSpace = match.endsWith(`>`) && match.length > `<${tag}>`.length;
      const suffix = hasSpace ? '' : ' ';
      return match.replace('>', `${suffix}style=\"${style}\">`);
    });
  }, html);

  return applyParentSpanContainerFix(result, tagStyles);
}

function applyAttributeSelectorStyles(html: string, selector: string, style: string): string {
  if (!selector.startsWith('span[')) {
    return html;
  }

  const attrMatch = selector.match(/span\\[(.+?)\\]/i);
  if (!attrMatch) {
    return html;
  }

  const attr = attrMatch[1];
  const regex = new RegExp(`<span([^>]*?)\\s${attr}([^>]*?)>`, 'gi');
  return html.replace(regex, match => {
    if (/style\\s*=/.test(match)) {
      return match.replace(/style\\s*=\\s*([\"'])(.*?)\\1/i, (_m, quote, existing) => {
        const combined = mergeStyles(existing, style);
        return `style=${quote}${combined}${quote}`;
      });
    }

    return match.replace('>', ` style=\"${style}\">`);
  });
}

function applyParentSpanContainerFix(html: string, tagStyles: Record<string, string>): string {
  const spanStyle = tagStyles['span[data-styles-container]'];
  if (!spanStyle) {
    return html;
  }

  return html.replace(/<(p|h[1-6]|ul|ol|li)([^>]*)>([\s\S]*?data-styles-container[\s\S]*?)<\/\1>/gi, (match, tag) => {
    const openTagRegex = new RegExp(`<${tag}([^>]*)>`, 'i');
    return match.replace(openTagRegex, full => {
      if (/style\\s*=/.test(full)) {
        return full.replace(/style\\s*=\\s*([\"'])(.*?)\\1/i, (_m, quote, existing) => {
          const combined = mergeStyles(existing, 'background-color:transparent');
          return `style=${quote}${combined}${quote}`;
        });
      }
      return full.replace('>', ` style=\"background-color:transparent\">`);
    });
  });
}

function applyHeadingSpanBackgrounds(html: string, cssVariables: Record<string, string | number>): string {
  if (!html) {
    return html;
  }

  let result = html;

  for (let level = 1; level <= 6; level += 1) {
    const backgroundColor =
      resolveVar(cssVariables[`--heading-${level}-background-color`], cssVariables) ?? 'transparent';
    const style = styleString({
      display: 'block',
      'background-color': backgroundColor,
    });

    if (!style) {
      continue;
    }

    const tagRegex = new RegExp(`<h${level}([^>]*)>([\\s\\S]*?)<\\/h${level}>`, 'gi');
    result = result.replace(tagRegex, (match, attrs, inner) => {
      const updatedInner = applyAttributeSelectorStyles(inner, 'span[data-styles-container]', style);
      return `<h${level}${attrs}>${updatedInner}</h${level}>`;
    });
  }

  return result;
}

function hasMarginDeclaration(style: string): boolean {
  return /(^|;)\s*margin(-top|-right|-bottom|-left)?\s*:/i.test(style);
}

function hasPaddingDeclaration(style: string): boolean {
  return /(^|;)\s*padding(-top|-right|-bottom|-left)?\s*:/i.test(style);
}

function applyEmailListStyles(html: string): string {
  if (!html || !/<(ul|ol)\b/i.test(html)) {
    return html;
  }

  const listStyle = styleString({
    'margin-bottom': '0',
    'margin-top': '0',
    'padding-left': '2rem',
  });

  return html.replace(/<(ul|ol)\b([^>]*)>/gi, match => upsertOpeningTagStyle(match, listStyle));
}

function upsertOpeningTagStyle(tag: string, style: string): string {
  if (!style) {
    return tag;
  }

  if (/style\s*=/.test(tag)) {
    return tag.replace(/style\s*=\s*(["'])(.*?)\1/i, (_match, quote, existing) => {
      const base = String(existing).trim().replace(/;+\s*$/, '');
      return `style=${quote}${mergeStyles(base, style)}${quote}`;
    });
  }

  return tag.replace('>', ` style="${style}">`);
}

function applyDefaultHeadingMargins(html: string, defaultMargin: string): string {
  if (!html) {
    return html;
  }

  const marginStyle = `margin:${defaultMargin};`;
  const paddingStyle = 'padding:0;';
  const defaultStyle = `${marginStyle}${paddingStyle}`;

  return html.replace(/<h([1-6])(\s[^>]*?)?>/gi, match => {
    if (/style\s*=/.test(match)) {
      return match.replace(/style\s*=\s*([\"'])(.*?)\1/i, (_m, quote, existing) => {
        if (hasMarginDeclaration(existing) && hasPaddingDeclaration(existing)) {
          return `style=${quote}${existing}${quote}`;
        }
        const combined = mergeStyles(existing, defaultStyle);
        return `style=${quote}${combined}${quote}`;
      });
    }

    return match.replace('>', ` style=\"${defaultStyle}\">`);
  });
}

function renderRow(
  row: any,
  common: {
    contentMaxWidth: number;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    socialMediaSettings?: { socials?: any[] };
    listIconPaths?: Record<string, string>;
    linkHoverCollector: LinkHoverStyleCollector;
  },
  cssVariables: Record<string, string | number>,
  helpers: Set<string>,
  warnings: string[],
): string {
  const rowType = row?.type;
  if (rowType === ROW_MODULE) {
    warnings.push('Email export: module rows are not supported in POC.');
    return '';
  }

  const container = row?.desktopContainerProperties ?? {};
  const padding = container?.padding ?? {};
  const rowBackground = container?.backgroundColor ?? common.backgroundColor;
  const defaultContentBackground = resolveVar(cssVariables['--contentBackgroundColor'], cssVariables) ?? '#fff';
  const contentBackground =
    container?.contentBackgroundColor === undefined ||
    container?.contentBackgroundColor === null ||
    container?.contentBackgroundColor === ''
      ? defaultContentBackground
      : container?.contentBackgroundColor;
  const minHeight = container?.minHeight ? `min-height:${container.minHeight}px;` : '';

  const contentStyles = styleString({
    'max-width': `${common.contentMaxWidth}px`,
    margin: '0 auto',
    'background-color': contentBackground,
    'padding-top': px(padding.paddingTop),
    'padding-right': px(padding.paddingRight),
    'padding-bottom': px(padding.paddingBottom),
    'padding-left': px(padding.paddingLeft),
  });

  const rowStyles = styleString({
    width: '100%',
    'background-color': rowBackground,
  });

  const columnsHtml = renderColumns(row?.columns ?? [], row, common, cssVariables, helpers, warnings);

  const html = `\n<table role=\"presentation\" width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${rowStyles}\">\n  <tr>\n    <td align=\"center\" style=\"padding:0;margin:0;${minHeight}\">\n      <table role=\"presentation\" class=\"tb-content\" width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${contentStyles}\">\n        <tr>\n          ${columnsHtml}\n        </tr>\n      </table>\n    </td>\n  </tr>\n</table>\n`;

  return wrapWithConditions(html, row?.conditions, helpers, warnings);
}

function renderColumns(
  columns: any[],
  row: any,
  common: {
    contentMaxWidth: number;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    socialMediaSettings?: { socials?: any[] };
    listIconPaths?: Record<string, string>;
    linkHoverCollector: LinkHoverStyleCollector;
  },
  cssVariables: Record<string, string | number>,
  helpers: Set<string>,
  warnings: string[],
): string {
  const commonColumnOptions = row?.desktopColumnOptions ?? {};
  const gap = Number(commonColumnOptions?.gap ?? 0);
  const halfGap = gap ? Math.floor(gap / 2) : 0;

  return columns
    .map(column => {
      const ratio = column?.ratio ?? 100;
      const columnOptions = column?.options ?? {};
      const columnBg = commonColumnOptions?.isIndividualBackgroundColor
        ? columnOptions?.backgroundColor
        : commonColumnOptions?.commonContentBackgroundColor;

      const alignItems = commonColumnOptions?.isIndividualHorizontalAlignments
        ? columnOptions?.horizontalAlignment
        : commonColumnOptions?.commonHorizontalContentAlignments;
      const verticalItems = commonColumnOptions?.isIndividualVerticalAlignments
        ? columnOptions?.verticalAlignment
        : commonColumnOptions?.commonVerticalContentAlignments;

      const styles = styleString({
        width: `${ratio}%`,
        'max-width': `${ratio}%`,
        'vertical-align': mapVerticalAlignment(verticalItems),
        'text-align': mapHorizontalAlignment(alignItems),
        'background-color': columnBg,
        'padding-left': px(halfGap),
        'padding-right': px(halfGap),
      });

      const widgetsHtml = (column?.items ?? [])
        .filter(Boolean)
        .map(widget => renderWidget(widget, common, cssVariables, helpers, warnings))
        .join('');

      return `<td class=\"tb-col\" valign=\"top\" style=\"${styles}\">${widgetsHtml}</td>`;
    })
    .join('');
}

function renderWidget(
  widget: any,
  common: {
    contentMaxWidth: number;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    socialMediaSettings?: { socials?: any[] };
    listIconPaths?: Record<string, string>;
    linkHoverCollector: LinkHoverStyleCollector;
  },
  cssVariables: Record<string, string | number>,
  helpers: Set<string>,
  warnings: string[],
): string {
  const widgetType = widget?.type;
  const container = widget?.desktopContainerProperties ?? {};
  const padding = container?.padding ?? {};

  const wrapperStyles = styleString({
    'padding-top': px(padding.paddingTop),
    'padding-right': px(padding.paddingRight),
    'padding-bottom': px(padding.paddingBottom),
    'padding-left': px(padding.paddingLeft),
    'background-color': container?.backgroundColor,
    color: container?.textColor ?? common.textColor,
    'font-family': common.fontFamily,
  });

  const data = widget?.desktopData ?? {};

  let html = '';

  switch (widgetType) {
    case WIDGET_TEXT:
    case WIDGET_HEADING: {
      const textHtml = replaceVariables(String(data?.innerHtml ?? ''));
      const linkFallbackColor =
        widgetType === WIDGET_HEADING
          ? resolveVar(cssVariables['--heading-1-color'], cssVariables)
          : resolveVar(cssVariables['--paragraph-color'], cssVariables);
      const htmlWithPerLinkStyles = applyPerLinkEmailStyles(
        textHtml,
        cssVariables,
        linkFallbackColor,
        common.linkHoverCollector,
      );
      const typographyStyles = styleString(buildTypographyStyles(widgetType, cssVariables));
      const combinedStyles = mergeStyles(wrapperStyles, typographyStyles);
      let styledHtml = applyInlineStylesToHtml(
        htmlWithPerLinkStyles,
        widgetType === WIDGET_HEADING ? buildHeadingTagStyles(cssVariables) : buildParagraphTagStyles(cssVariables),
      );
      if (widgetType === WIDGET_HEADING) {
        styledHtml = applyHeadingSpanBackgrounds(styledHtml, cssVariables);
        styledHtml = applyDefaultHeadingMargins(styledHtml, DEFAULT_HEADING_MARGIN);
      }
      return wrapWithConditions(
        `<div style=\"${combinedStyles}\">${styledHtml}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );
    }

    case WIDGET_IMAGE:
      return wrapWithConditions(
        `<div style=\"${wrapperStyles}\">${renderImageWidget(data, helpers)}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );

    case 'list': {
      const listData = { ...data };
      for (const key of ['headingAppearance', 'descriptionAppearance', 'iconAppearance']) {
        listData[key] = { ...data[key], color: resolveVar(data[key]?.color, cssVariables) };
      }
      listData.cardsBackground = resolveVar(data.cardsBackground, cssVariables);
      return wrapWithConditions(
        `<div style="${escapeHtml(wrapperStyles)}">${renderListEmailWidget(listData, helpers, common.listIconPaths)}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );
    }

    case WIDGET_BUTTON:
      return wrapWithConditions(
        `<div style=\"${wrapperStyles}\">${renderButtonWidget(data, cssVariables)}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );

    case WIDGET_SEPARATOR:
      return wrapWithConditions(
        `<div style=\"${wrapperStyles}\">${renderSeparatorWidget(data)}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );

    case WIDGET_SPACER:
      return wrapWithConditions(
        `<div style=\"${wrapperStyles}\">${renderSpacerWidget(data)}</div>`,
        widget?.conditions,
        helpers,
        warnings,
      );

    case WIDGET_EMAIL_REGISTRATION_DETAILS_TABLE:
      html = renderRegistrationDetailsTable(helpers, resolveTableBackgroundVariables(resolveEmailRegistrationTableSettings(data), cssVariables));
      break;

    case WIDGET_EMAIL_EXCURSIONS_TABLE:
      html = renderExcursionsTable(helpers, resolveTableBackgroundVariables(resolveEmailExcursionsTableSettings(data), cssVariables), widget?.id);
      break;

    case WIDGET_EMAIL_TRANSACTIONS_TABLE:
      html = renderTransactionsTable(helpers, resolveTableBackgroundVariables(resolveEmailTransactionTableSettings(data), cssVariables));
      break;

    case WIDGET_EMAIL_ACTIVITIES_FOR_DAY:
      html = renderActivitiesForDay(widget?.id);
      break;

    case WIDGET_EMAIL_FORM_RESULTS_TABLE:
      html = renderFormResultsTable(data?.formResultsTable?.textStyles);
      break;

    case WIDGET_EMAIL_CONFERENCE_IMAGE:
      html = renderConferenceImageWidget(helpers, data);
      break;

    case WIDGET_SOCIAL_MEDIA:
      html = renderSocialMediaWidget(data, helpers, common?.socialMediaSettings);
      break;

    default:
      warnings.push(`Email export: unsupported widget type "${String(widgetType)}" was skipped.`);
      return '';
  }

  return wrapWithConditions(`<div style=\"${wrapperStyles}\">${html}</div>`, widget?.conditions, helpers, warnings);
}

function renderImageWidget(data: any, helpers: Set<string>): string {
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) {
    return '';
  }

  helpers.add('inlineImage');

  const width = Number(data?.width ?? 100);
  const gap = Number(data?.gap ?? 0);
  const isGridMode = String(data?.mode ?? '') === 'grid';

  return items
    .map((item, index) => {
      const path = item?.wasabiPath ?? '';
      const src = path ? `{{inlineImage ${JSON.stringify(path)}}}` : '';
      const imgStyles = styleString({
        // Keep image inline-level so parent td text-align controls left/center/right alignment.
        display: 'inline-block',
        width: `${width}%`,
        'max-width': '100%',
        border: '0',
      });

      const imageTag = `<img src=\"${src}\" style=\"${imgStyles}\" alt=\"\" />`;
      const imageWithAction =
        data?.actionType === 'link' && data?.actionPayload?.href
          ? `<a href=\"${String(data.actionPayload.href)}\" style=\"text-decoration:none;\">${imageTag}</a>`
          : imageTag;

      if (isGridMode) {
        const isLast = index === items.length - 1;
        return `<span style=\"${styleString({
          'padding-right': isLast ? 0 : px(gap),
        })}\">${imageWithAction}</span>`;
      }

      return `<div>${imageWithAction}</div>`;
    })
    .join('');
}

function renderButtonWidget(data: any, cssVariables: Record<string, string | number>): string {
  const href = resolveButtonHref(data) ?? '';

  const type = String(data?.type ?? 'default').trim() || 'default';
  const stylingMode = String(data?.stylingMode ?? 'contained').trim() || 'contained';
  const text = String(data?.text ?? '').trim() || 'Button Text';
  const alignItems = typeof data?.alignItems === 'string' ? data.alignItems.trim() : '';
  const target = String(data?.actionPayload?.target ?? '_blank').trim() || '_blank';
  const icon = String(data?.icon ?? '').trim();

  const align = mapHorizontalAlignment(alignItems);
  const accentColor = resolveVar(data?.color ?? `var(--pb-${type}-accent)`, cssVariables) ?? '#23a3ea';
  const invertedColor = resolveVar(data?.invertedColor ?? `var(--pb-${type}-inverted-text)`, cssVariables) ?? '#ffffff';
  const fontFamily =
    normalizeFontFamily(resolveVar(data?.fontFamily ?? `var(--pb-${type}-font-family)`, cssVariables)) ??
    DEFAULT_FONT_FAMILY;
  const borderRadius = Number(data?.borderRadius);
  const resolvedBorderRadius = Number.isFinite(borderRadius)
    ? `${borderRadius}px`
    : resolveVar(`var(--pb-${type}-border-radius)`, cssVariables) ?? '4px';

  const emphasis = new Set(Array.isArray(data?.fontEmphasis) ? data.fontEmphasis.map(item => String(item)) : []);
  const fontWeight = emphasis.has('bold') ? 'bold' : resolveVar(`var(--pb-${type}-font-weight)`, cssVariables) ?? '600';
  const fontStyle = emphasis.has('italic')
    ? 'italic'
    : resolveVar(`var(--pb-${type}-font-style)`, cssVariables) ?? 'normal';
  const textDecoration = [
    emphasis.has('underline') ? 'underline' : '',
    emphasis.has('strikethrough') ? 'line-through' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isText = stylingMode === 'text';
  const isOutlined = stylingMode === 'outlined';
  const linkStyles = styleString({
    display: 'inline-block',
    'text-decoration': textDecoration || 'none',
    'font-family': String(fontFamily),
    'font-weight': String(fontWeight),
    'font-style': String(fontStyle),
    'line-height': '1.2858',
    'font-size': '14px',
    padding: isText ? '0' : '8px 12px',
    'border-radius': isText ? undefined : resolvedBorderRadius,
    border: isText ? 'none' : `1px solid ${accentColor}`,
    'background-color': isText ? 'transparent' : isOutlined ? 'transparent' : accentColor,
    color: isText || isOutlined ? accentColor : invertedColor,
  });

  const iconClass = icon ? escapeHtml(icon.startsWith('agi-') ? icon : `dx-icon dx-icon-${icon}`) : '';
  const iconColor = isText || isOutlined ? accentColor : invertedColor;
  const iconHtml = iconClass
    ? `<i class="${iconClass}" style="${styleString({
        color: iconColor,
        'margin-right': text ? '9px' : '0',
        'font-size': '18px',
        'line-height': '1',
        'vertical-align': 'middle',
        display: 'inline-block',
      })}"></i>`
    : '';
  const textHtml = text ? `<span style="vertical-align:middle;">${escapeHtml(text)}</span>` : '';
  const buttonContentHtml = `${iconHtml}${textHtml}`;

  const targetAttr = data?.actionType === 'link' ? ` target=\"${escapeHtml(target)}\"` : '';
  const relAttr = target === '_blank' ? ' rel="noopener noreferrer"' : '';
  return (
    `<div style=\"${styleString({ 'text-align': align, margin: '0' })}\">` +
    `<a href=\"${escapeHtml(href)}\"${targetAttr}${relAttr} style=\"${linkStyles}\">${buttonContentHtml}</a>` +
    '</div>'
  );
}
// Added button widget rendering support handles link, mailTo and phone for emails
function resolveButtonHref(data: any): string {
  const actionType = String(data?.actionType ?? 'link');
  const actionPayload = data?.actionPayload ?? {};

  if (actionType === 'mailTo') {
    const to = String(actionPayload?.to ?? '').trim();
    if (!to) {
      return '';
    }
    const subject = String(actionPayload?.subject ?? '').trim();
    const body = String(actionPayload?.body ?? '').trim();
    const query = new URLSearchParams();
    subject && query.append('subject', subject);
    body && query.append('body', body);
    const queryString = query.toString();
    return `mailto:${to}${queryString ? `?${queryString}` : ''}`;
  }

  if (actionType === 'phone') {
    const number = String(actionPayload?.number ?? '').trim();
    if (!number) {
      return '';
    }
    return `tel:${number}`;
  }

  const href = String(actionPayload?.href ?? '').trim();
  return href || '';
}

function renderSeparatorWidget(data: any): string {
  const color = data?.color ?? '#000';
  const width = data?.width ?? '100%';

  return `<div style=\"border-top:1px solid ${color};width:${width};margin:0 auto;\"></div>`;
}

function renderSpacerWidget(data: any): string {
  const height = Number(data?.height ?? 16);
  const styles = styleString({
    height: px(height),
    'line-height': px(height),
  });

  return `<div style=\"${styles}\">&nbsp;</div>`;
}

function renderConferenceImageWidget(helpers: Set<string>, data?: any): string {
  helpers.add('inlineImage');

  const imgStyles = styleString({
    display: 'block',
    width: '100%',
    'max-width': '100%',
    border: '0',
  });
  const wrapperStyles = styleString({ margin: '10px 0' });

  const widgetPath =
    typeof data?.imagePath === 'string'
      ? data.imagePath
      : typeof data?.imagePath?.wasabiPath === 'string'
        ? data.imagePath.wasabiPath
        : '';
  if (widgetPath) {
    return (
      `<div style=\"${wrapperStyles}\">` +
      `<img src=\"{{inlineImage ${JSON.stringify(widgetPath)}}}\" style=\"${imgStyles}\" alt=\"{{conference.event_name}}\" />` +
      '</div>'
    );
  }

  return (
    '{{#if conference.welcomeImagePath}}' +
    `<div style=\"${wrapperStyles}\">` +
    `<img src=\"{{inlineImage conference.welcomeImagePath}}\" style=\"${imgStyles}\" alt=\"{{conference.event_name}}\" />` +
    '</div>' +
    '{{/if}}'
  );
}

function renderRegistrationDetailsTable(helpers: Set<string>, settings: ResolveTableOptions): string {
  helpers.add('ifEven');

  const titleHtml = settings?.title
    ? `<div style=\"margin:0 0 8px;font-size:16px;line-height:1.3;font-weight:600;color:${DEFAULT_TABLE_TEXT_COLOR};${styleString(tableTextStyle(settings?.textStyles, 'title'))}\">${escapeHtml(
        settings.title,
      )}</div>`
    : '';
  const tableStyles = buildDataTableStyles(settings?.colorScheme, settings?.textStyles);

  return (
    '{{#if registrationDetails}}' +
    '<div style="margin:10px 0; overflow-x:auto; max-width:100%; -webkit-overflow-scrolling:touch;">' +
    titleHtml +
    `<table width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${mergeStyles(
      tableStyles.table,
      styleString({
        'table-layout': 'fixed',
      }),
    )}\">` +
    '<thead>' +
    `<tr style=\"${tableStyles.headerRow}\">` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:150px;')}\">First Name</th>` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:150px;')}\">Last Name</th>` +
    `<th style=\"${tableStyles.headerCell}\">Registration Type</th>` +
    '</tr>' +
    '</thead>' +
    '<tbody>' +
    '{{#each registrationDetails}}' +
    `<tr {{#ifEven @index}}style=\"${tableStyles.zebraRow}\"{{else}}style=\"${tableStyles.groupRow}\" {{/ifEven}}>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:150px;')}\">{{first_name}}</td>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:150px;')}\">{{last_name}}</td>` +
    `<td style=\"${tableStyles.cell}\">{{registrationType}}</td>` +
    '</tr>' +
    '{{/each}}' +
    '</tbody>' +
    '</table>' +
    '</div>' +
    '{{/if}}'
  );
}

function renderExcursionsTable(helpers: Set<string>, settings: ResolveTableOptions, widgetId?: string): string {
  helpers.add('ifEven');

  const includePreferences = settings?.includePreferences !== false;
  const titleHtml = settings?.title
    ? `<div style=\"margin:0 0 8px;font-size:16px;line-height:1.3;font-weight:600;color:${DEFAULT_TABLE_TEXT_COLOR};${styleString(tableTextStyle(settings?.textStyles, 'title'))}\">${escapeHtml(
        settings.title,
      )}</div>`
    : '';
  const tableStyles = buildDataTableStyles(settings?.colorScheme, settings?.textStyles);
  const excursionsVar = widgetId ? `excursions_${widgetId}` : 'excursions';
  const columnCount = includePreferences ? 3 : 2;

  const preferencesHeader = includePreferences
    ? `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:250px;')}\">Preferences</th>`
    : '';

  const preferencesCell = includePreferences
    ? `<td style=\"${mergeStyles(tableStyles.cell, 'width:250px;')}\">{{excursionPreferences}}</td>`
    : '';

  return (
    `{{#if ${excursionsVar}}}` +
    '<div style="margin:10px 0; overflow-x:auto; max-width:100%; -webkit-overflow-scrolling:touch;">' +
    titleHtml +
    `<table width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${mergeStyles(
      tableStyles.table,
      styleString({
        'table-layout': 'fixed',
      }),
    )}\">` +
    '<thead>' +
    `<tr style=\"${tableStyles.headerRow}\">` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:150px;')}\">Attendee</th>` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:150px;')}\">Excursion</th>` +
    preferencesHeader +
    '</tr>' +
    '</thead>' +
    '<tbody>' +
    `{{#each ${excursionsVar}}}` +
    `<tr style=\"${tableStyles.groupRow}\">` +
    `<td colspan=\"${columnCount}\" style=\"${mergeStyles(tableStyles.cell, 'width:100%;')}\">{{excursionsGroupedDate}}</td>` +
    '</tr>' +
    '{{#each excursionsGroup}}' +
    `<tr {{#ifEven @index}}{{else}}style=\"${tableStyles.zebraRow}\"{{/ifEven}}>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:150px;')}\">{{excursionAttendee}}</td>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:150px;')}\">{{excursionName}}</td>` +
    preferencesCell +
    '</tr>' +
    '{{/each}}' +
    '{{/each}}' +
    '</tbody>' +
    '</table>' +
    '</div>' +
    '{{/if}}'
  );
}

function renderTransactionsTable(helpers: Set<string>, settings: ResolveTableOptions): string {
  helpers.add('ifEven');

  const tableStyles = buildDataTableStyles(settings?.colorScheme, settings?.textStyles);
  const titleHtml = settings?.title
    ? `<div style=\"margin:0 0 8px;font-size:16px;line-height:1.3;font-weight:600;color:${DEFAULT_TABLE_TEXT_COLOR};${styleString(tableTextStyle(settings?.textStyles, 'title'))}\">${escapeHtml(
        settings.title,
      )}</div>`
    : '';

  return (
    '{{#if transactions}}' +
    '<div style="margin:10px 0; overflow-x:auto; max-width:100%; -webkit-overflow-scrolling:touch;">' +
    titleHtml +
    `<table width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${mergeStyles(
      tableStyles.table,
      styleString({
        'table-layout': 'fixed',
      }),
    )}\">` +
    '<thead>' +
    `<tr style=\"${tableStyles.headerRow}\">` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:30%;')}\">Transaction Date</th>` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:25%;')}\">Total</th>` +
    `<th style=\"${mergeStyles(tableStyles.headerCell, 'width:45%;')}\">Payment Status</th>` +
    '</tr>' +
    '</thead>' +
    '<tbody>' +
    '{{#each transactions}}' +
    `<tr {{#ifEven @index}}style=\"${tableStyles.zebraRow}\"{{else}}style=\"${tableStyles.groupRow}\" {{/ifEven}}>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:30%;')}\">{{transactionDate}}</td>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:25%;')}\">{{transactionTotal}}</td>` +
    `<td style=\"${mergeStyles(tableStyles.cell, 'width:45%;')}\">{{transactionStatus}}</td>` +
    '</tr>' +
    '{{/each}}' +
    '</tbody>' +
    '</table>' +
    '</div>' +
    '{{/if}}'
  );
}

function renderActivitiesForDay(widgetId?: string): string {
  const activitiesVar = widgetId ? `activitiesForDay_${widgetId}` : 'activitiesForDay';
  const formattedDayVar = widgetId ? `formattedActivitiesDay_${widgetId}` : 'formattedActivitiesDay';
  return (
    `{{#if ${activitiesVar}}}` +
    '<div style="line-height:1.1;">' +
    `<h3 style=\"margin:10px 0;text-decoration:underline;\">{{${formattedDayVar}}} Excursions</h3>` +
    `{{#each ${activitiesVar}}}` +
    '<h3 style="margin:0 0 1px;line-height:1.1;">{{name}}</h3>' +
    '<p style="margin:4px 0 10px;line-height:1.1;">{{{shortDescription}}}</p>' +
    '{{/each}}' +
    '</div>' +
    '{{/if}}'
  );
}

function renderFormResultsTable(textStyles?: TableTextStyles): string {
  const tableStyles = buildDataTableStyles(undefined, textStyles);
  const keyCellStyles = mergeStyles(
    tableStyles.headerCell,
    styleString({
      'font-weight': '600',
      'text-transform': 'capitalize',
      'background-color': DEFAULT_TABLE_ZEBRA_BG,
      'width': '35%',
      ...tableTextStyle(textStyles, 'header'),
    }),
  );

  return (
    '{{#if formData}}' +
    '<div style="overflow-x:auto; max-width:100%; -webkit-overflow-scrolling:touch;">' +
    `<table width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"${tableStyles.table}\">` +
    '{{#each formData}}' +
    '<tr>' +
    `<td style=\"${keyCellStyles}\">{{@key}}</td>` +
    `<td style=\"${tableStyles.cell}\">{{this}}</td>` +
    '</tr>' +
    '{{/each}}' +
    '</table>' +
    '</div>' +
    '{{/if}}'
  );
}

function replaceVariables(html: string): string {
  if (!html) {
    return html;
  }

  const openTagRegex = /<span\b[^>]*class=["'][^"']*\b(?:ag-variable-blot|dx-mention)\b[^"']*["'][^>]*>/gi;
  const spanTokenRegex = /<\/?span\b[^>]*>/gi;

  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = openTagRegex.exec(html))) {
    const start = match.index;
    const openTag = match[0];
    const closeIndex = findMatchingSpanClose(html, openTagRegex.lastIndex, spanTokenRegex);

    if (closeIndex === -1) {
      break;
    }

    result += html.slice(lastIndex, start);

    const isMention = /dx-mention/i.test(openTag);
    const mentionPayload = isMention ? decodeMentionPayload(readAttribute(openTag, 'data-id')) : null;
    const value =
      mentionPayload?.value ?? readAttribute(openTag, 'data-mention-value') ?? readAttribute(openTag, 'data-value');
    const collection = mentionPayload?.collection ?? readAttribute(openTag, 'data-collection');

    if (value) {
      result += collection ? `{{${collection}.${value}}}` : `{{${value}}}`;
    }

    lastIndex = closeIndex;
    openTagRegex.lastIndex = closeIndex;
  }

  result += html.slice(lastIndex);
  return result;
}

function decodeMentionPayload(
  rawValue?: string | null,
): { value?: string; collection?: string; description?: string } | null {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(decodeURIComponent(rawValue));
  } catch (error) {
    try {
      return JSON.parse(rawValue);
    } catch (nestedError) {
      return null;
    }
  }
}

function findMatchingSpanClose(html: string, fromIndex: number, tokenRegex: RegExp): number {
  let depth = 1;
  tokenRegex.lastIndex = fromIndex;

  let token: RegExpExecArray | null;
  while ((token = tokenRegex.exec(html))) {
    const tag = token[0];
    if (/^<\//.test(tag)) {
      depth -= 1;
    } else {
      depth += 1;
    }

    if (depth === 0) {
      return tokenRegex.lastIndex;
    }
  }

  return -1;
}

function readAttribute(tag: string, name: string): string | undefined {
  const regex = new RegExp(`${name}\\s*=\\s*(\"([^\"]*)\"|'([^']*)'|([^\\s>]+))`, 'i');
  const match = tag.match(regex);
  if (!match) {
    return undefined;
  }
  return match[2] ?? match[3] ?? match[4];
}

function wrapWithConditions(html: string, conditions: any, helpers: Set<string>, warnings: string[]): string {
  if (!html) {
    return html;
  }

  const rules = conditions?.rules?.filter(rule => rule?.field);
  if (!rules?.length) {
    return html;
  }

  const expressions: string[] = [];
  rules.forEach(rule => {
    const expression = buildRuleExpression(rule, helpers, warnings);
    if (expression) {
      expressions.push(expression);
    }
  });

  if (!expressions.length) {
    return html;
  }

  const mode = conditions?.mode === 'any' ? 'or' : 'and';
  if (expressions.length > 1) {
    helpers.add(mode);
  }

  const conditionExpression = expressions.length === 1 ? expressions[0] : `(${mode} ${expressions.join(' ')})`;

  return `{{#if ${conditionExpression}}}${html}{{/if}}`;
}

function buildRuleExpression(rule: any, helpers: Set<string>, warnings: string[]): string | null {
  const field = String(rule?.field ?? '').trim();
  if (!field) {
    return null;
  }

  switch (rule?.operator) {
    case 'exists':
      helpers.add('exists');
      return `(exists ${field})`;
    case 'isTrue':
      helpers.add('isTrue');
      return `(isTrue ${field})`;
    case 'isFalse':
      helpers.add('isFalse');
      return `(isFalse ${field})`;
    case 'equals':
      helpers.add('eq');
      return `(eq ${field} ${serializeValue(rule?.value)})`;
    case 'notEquals':
      helpers.add('ne');
      return `(ne ${field} ${serializeValue(rule?.value)})`;
    default:
      warnings.push(`Email export: unsupported condition operator "${String(rule?.operator)}" was ignored.`);
      return null;
  }
}

function serializeValue(value: any): string {
  const normalized = coerceValue(value);
  return JSON.stringify(normalized ?? '');
}

function coerceValue(value: any): any {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === 'true') {
      return true;
    }
    if (trimmed === 'false') {
      return false;
    }
    if (trimmed && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }
    return value;
  }
  return value;
}

function px(value: any): string | undefined {
  if (value === 0) {
    return '0px';
  }
  if (!value && value !== 0) {
    return undefined;
  }
  return `${value}px`;
}

function buildDataTableStyles(colorScheme?: { header?: string; color1?: string; color2?: string }, textStyles?: TableTextStyles) {
  const headerBg = colorScheme?.header || DEFAULT_TABLE_HEADER_BG;
  const rowBg1 = colorScheme?.color1 || DEFAULT_TABLE_GROUP_BG;
  const rowBg2 = colorScheme?.color2 || DEFAULT_TABLE_ZEBRA_BG;

  const table = styleString({
    width: '100%',
    'border-collapse': 'collapse',
    'border-spacing': '0',
    'font-family': DEFAULT_FONT_FAMILY,
    'font-size': '14px',
    'line-height': '1.4',
    color: DEFAULT_TABLE_TEXT_COLOR,
    'background-color': '#ffffff',
  });

  const baseCell = styleString({
    'padding': '6px 8px',
    'border': `1px solid ${DEFAULT_TABLE_BORDER_COLOR}`,
    'text-align': 'left',
    'vertical-align': 'top',
    'word-break': 'break-word',
    'word-wrap': 'break-word',
  });

  const cell = mergeStyles(baseCell, styleString(tableTextStyle(textStyles, 'body')));
  const headerCell = mergeStyles(
    baseCell,
    styleString({
      'font-weight': '600',
      ...tableTextStyle(textStyles, 'header', headerBg),
    }),
  );

  const headerRow = styleString({
    'background-color': headerBg,
    'color': tableTextColor(headerBg),
    'font-weight': '600',
  });

  const zebraRow = styleString({
    'background-color': rowBg2,
    'color': tableTextColor(rowBg2),
  });

  const groupRow = styleString({
    'background-color': rowBg1,
    'color': tableTextColor(rowBg1),
    'font-weight': '600',
  });

  return {
    table,
    cell,
    headerCell,
    headerRow,
    zebraRow,
    groupRow,
  };
}

function resolveEmailExcursionsTableSettings(data: any): ResolveTableOptions {
  const tableData = data?.excursionsTable ?? {};
  const includePreferences = tableData?.includePreferences ?? data?.includePreferences ?? true;
  const { title, colorScheme } = resolveEmailTableTitleAndColorScheme(tableData);

  return {
    title,
    textStyles: tableData?.textStyles,
    includePreferences,
    colorScheme,
  };
}

function resolveEmailRegistrationTableSettings(data: any): ResolveTableOptions {
  const tableData = data?.registrationDetailsTable ?? {};
  const { title, colorScheme } = resolveEmailTableTitleAndColorScheme(tableData);

  return {
    title,
    textStyles: tableData?.textStyles,
    colorScheme,
  };
}

function resolveEmailTransactionTableSettings(data: any): ResolveTableOptions {
  const tableData = data?.transactionTable ?? {};
  const { title, colorScheme } = resolveEmailTableTitleAndColorScheme(tableData);

  return {
    title,
    textStyles: tableData?.textStyles,
    colorScheme,
  };
}

function resolveEmailTableTitleAndColorScheme(tableData: any): ResolveTableOptions {
  const title = tableData?.title ?? '';
  const selectedStyle = String(tableData?.selectedStyle ?? DEFAULT_EMAIL_TABLE_STYLE);
  const customColorScheme = tableData?.customColorScheme;

  if (selectedStyle === TableStylePreset.Custom) {
    return {
      title,
      colorScheme: {
        header: customColorScheme?.header || DEFAULT_TABLE_HEADER_BG,
        color1: customColorScheme?.color1 || DEFAULT_TABLE_GROUP_BG,
        color2: customColorScheme?.color2 || DEFAULT_TABLE_ZEBRA_BG,
      },
    };
  }

  const colorScheme =
    DEFAULT_EMAIL_TABLE_STYLES[selectedStyle] ?? DEFAULT_EMAIL_TABLE_STYLES[DEFAULT_EMAIL_TABLE_STYLE];

  return {
    title,
    colorScheme,
  };
}

function resolveTableBackgroundVariables(
  settings: ResolveTableOptions,
  variables: Record<string, string | number>,
): ResolveTableOptions {
  return {
    ...settings,
    colorScheme: {
      header: resolveVar(settings.colorScheme?.header, variables),
      color1: resolveVar(settings.colorScheme?.color1, variables),
      color2: resolveVar(settings.colorScheme?.color2, variables),
    },
  };
}
